import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  TextField,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Switch,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSearch } from '../Keyvault/SearchContext';
import {
  getModuleList,
  getTenantList,
  updateTenantModuleStatus,
} from '../../apis/tenantDetailsApi/tenantDetailsApi';

interface Module {
  Module_Id: number;
  Module_Nm: string;
  Active_Flg: boolean;
}

const TenantModules: React.FC = () => {
  const { instanceName } = useParams<{ instanceName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { searchText, setSearchText } = useSearch();

  const tenantId = location.state?.tenantId;

  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [updating, setUpdating] = useState(false); //  For individual toggle API call loading

  //  Fetch modules and tenant data
  useEffect(() => {
    if (!tenantId) return;

    const fetchModules = async () => {
      try {
        setLoading(true);

        const [moduleResp, tenantResp] = await Promise.all([
          getModuleList(),
          getTenantList(Number(tenantId)),
        ]);

        if (moduleResp.success && tenantResp.success) {
          const tenantModules = tenantResp.data[0]?.modules || [];

          const mappedModules: Module[] = moduleResp.data.map((mod: any) => {
            const tenantModule = tenantModules.find(
              (tMod: any) =>
                tMod.Module_Nm.toLowerCase() === mod.Module_Nm.toLowerCase()
            );

            return {
              Module_Id: mod.Id,
              Module_Nm: mod.Module_Nm,
              Active_Flg: tenantModule ? tenantModule.Active_Flg === 'Y' : false,
            };
          });

          setModules(mappedModules);
        }
      } catch (error) {
        console.error('Error fetching modules:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, [tenantId]);

  //  Open confirmation dialog
  const handleOpenConfirm = (module: Module) => {
    setSelectedModule(module);
    setDialogOpen(true);
  };

  //  Confirm and call API to toggle
  const handleConfirmToggle = async () => {
    if (!selectedModule || !tenantId) return;

    const newStatus = !selectedModule.Active_Flg;

    try {
      setUpdating(true);

      // Call API to update
      const resp = await updateTenantModuleStatus({
        tenantId: tenantId,
        moduleId: selectedModule.Module_Id,
        activeFlg: newStatus,
      });

      if (resp.success) {
        //  Update UI after success
        setModules((prev) =>
          prev.map((mod) =>
            mod.Module_Id === selectedModule.Module_Id
              ? { ...mod, Active_Flg: newStatus }
              : mod
          )
        );
      } else {
        console.error('Failed to update module:', resp);
      }
    } catch (error) {
      console.error('Error updating module status:', error);
    } finally {
      setUpdating(false);
      setDialogOpen(false);
      setSelectedModule(null);
    }
  };

  const handleCancelToggle = () => {
    setDialogOpen(false);
    setSelectedModule(null);
  };

  const filteredModules = modules.filter((mod) =>
    mod.Module_Nm.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Box sx={{ bgcolor: '#f5f5f5', overflowY: 'auto', p: 4, minHeight: '100vh' }}>
      {/* Search Bar */}
      <Box display="flex" justifyContent="center" mb={3}>
        <TextField
          variant="outlined"
          placeholder="Search..."
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            style: {
              background: 'white',
              borderRadius: 8,
              minWidth: 300,
              width: '100%',
              maxWidth: 500,
            },
          }}
        />
      </Box>

      {/* Header */}
      <Box display="flex" alignItems="center" mb={3} justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <IconButton
            onClick={() =>
              navigate(`/TenantDetails/${instanceName}`, {
                state: { envDetails: location.state?.envDetails },
              })
            }
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ color: '#734bd1', fontWeight: 'bold' }}>
            Env Name: {instanceName}
          </Typography>
        </Box>
      </Box>

      {/* Module Toggles */}
      <Card sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, color: '#734bd1', fontWeight: 'bold' }}
        >
          Module Toggles
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress sx={{ color: '#734bd1' }} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredModules.map((module) => (
              <Grid item xs={12} sm={6} md={4} key={module.Module_Id}>
                <Paper
                  elevation={1}
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: 2,
                    backgroundColor: '#fafafa',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Tooltip title={module.Module_Nm} placement="top-start" arrow>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        color: '#734bd1',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '70%',
                      }}
                    >
                      {module.Module_Nm}
                    </Typography>
                  </Tooltip>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={module.Active_Flg}
                        onChange={() => handleOpenConfirm(module)}
                        disabled={updating} //  Disable while API call in progress
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#734bd1' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#734bd1',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: module.Active_Flg ? 'green' : 'red',
                        }}
                      >
                        {module.Active_Flg ? 'Enabled' : 'Disabled'}
                      </Typography>
                    }
                    labelPlacement="start"
                    sx={{ ml: 1 }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelToggle}
        PaperProps={{
          sx: { p: 2, borderRadius: 2, minWidth: 350, maxWidth: 400 },
        }}
      >
        <Box display="flex" justifyContent="flex-end">
          <IconButton onClick={handleCancelToggle} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogTitle
          sx={{ fontWeight: 'bold', color: '#734bd1', textAlign: 'center' }}
        >
          Confirm Toggle
        </DialogTitle>

        <DialogContent>
          <Typography textAlign="center">
            Are you sure you want to{' '}
            <strong>{selectedModule?.Active_Flg ? 'disable' : 'enable'}</strong> the <br />
            <strong>{selectedModule?.Module_Nm}</strong> module?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', gap: 2 }}>
          <Button
            onClick={handleCancelToggle}
            variant="outlined"
            sx={{ borderColor: '#734bd1', color: '#734bd1' }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirmToggle}
            variant="contained"
            sx={{ backgroundColor: '#734bd1' }}
            disabled={updating}
          >
            {updating ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              'Yes'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantModules;
